import React, { useCallback, useEffect, useRef, useState } from "react";
import { plainTextFromHtml, sanitizeEmailHtml } from "../../utils/emailHtml";

function exec(cmd, value = null) {
  try {
    document.execCommand(cmd, false, value);
  } catch {
    /* ignore */
  }
}

/**
 * Rich compose area (HTML). Toolbar uses execCommand for broad browser support without new deps.
 */
export function AdminEmailRichComposer({
  disabled = false,
  placeholder = "Write your message…",
  valueHtml = "",
  onChangeHtml,
  className = "",
}) {
  const ref = useRef(null);
  const fileRef = useRef(null);
  const [empty, setEmpty] = useState(true);
  const [charCount, setCharCount] = useState(0);

  const syncEmpty = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const text = (el.textContent || "").replace(/\u00a0/g, " ").trim();
    setEmpty(!text);
    setCharCount(plainTextFromHtml(el.innerHTML).length);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const cur = el.innerHTML.trim();
    const next = (valueHtml || "").trim();
    if (next !== cur && (next || !cur)) {
      el.innerHTML = next || "";
      syncEmpty();
    }
  }, [valueHtml, syncEmpty]);

  const emit = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    onChangeHtml?.(sanitizeEmailHtml(el.innerHTML));
    syncEmpty();
  }, [onChangeHtml, syncEmpty]);

  const onInput = useCallback(() => {
    emit();
  }, [emit]);

  const onPaste = useCallback(
    (e) => {
      e.preventDefault();
      const t = e.clipboardData.getData("text/plain");
      if (t) exec("insertText", t);
      emit();
    },
    [emit]
  );

  const insertImageFromFile = useCallback(
    (file) => {
      if (!file || !file.type.startsWith("image/")) return;
      const reader = new FileReader();
      reader.onload = () => {
        const url = String(reader.result || "");
        if (!url) return;
        exec("insertHTML", `<p><img src="${url}" alt="" style="max-width:100%;height:auto;border-radius:8px" /></p>`);
        emit();
      };
      reader.readAsDataURL(file);
    },
    [emit]
  );

  return (
    <div className={`admin-dash__email-rich ${className}`.trim()}>
      <div className="admin-dash__email-rich-toolbar" role="toolbar" aria-label="Formatting">
        <button
          type="button"
          className="admin-dash__email-rich-tool"
          disabled={disabled}
          onClick={() => {
            ref.current?.focus();
            exec("bold");
            emit();
          }}
          title="Bold"
        >
          <strong>B</strong>
        </button>
        <button
          type="button"
          className="admin-dash__email-rich-tool"
          disabled={disabled}
          onClick={() => {
            ref.current?.focus();
            exec("italic");
            emit();
          }}
          title="Italic"
        >
          <em>I</em>
        </button>
        <button
          type="button"
          className="admin-dash__email-rich-tool"
          disabled={disabled}
          onClick={() => {
            ref.current?.focus();
            exec("underline");
            emit();
          }}
          title="Underline"
        >
          <span style={{ textDecoration: "underline" }}>U</span>
        </button>
        <span className="admin-dash__email-rich-toolbar-sep" aria-hidden />
        <button
          type="button"
          className="admin-dash__email-rich-tool"
          disabled={disabled}
          onClick={() => {
            ref.current?.focus();
            exec("hiliteColor", "#fef08a");
            emit();
          }}
          title="Highlight"
        >
          Hi
        </button>
        <span className="admin-dash__email-rich-toolbar-sep" aria-hidden />
        <button
          type="button"
          className="admin-dash__email-rich-tool"
          disabled={disabled}
          onClick={() => {
            ref.current?.focus();
            exec("insertUnorderedList");
            emit();
          }}
          title="Bullet list"
        >
          • List
        </button>
        <button
          type="button"
          className="admin-dash__email-rich-tool"
          disabled={disabled}
          onClick={() => {
            ref.current?.focus();
            exec("insertOrderedList");
            emit();
          }}
          title="Numbered list"
        >
          1. List
        </button>
        <span className="admin-dash__email-rich-toolbar-sep" aria-hidden />
        <button
          type="button"
          className="admin-dash__email-rich-tool"
          disabled={disabled}
          onClick={() => {
            ref.current?.focus();
            const url = window.prompt("Link URL (https://…)");
            if (url && url.trim()) exec("createLink", url.trim());
            emit();
          }}
          title="Insert link"
        >
          Link
        </button>
        <button
          type="button"
          className="admin-dash__email-rich-tool"
          disabled={disabled}
          onClick={() => fileRef.current?.click()}
          title="Insert image"
        >
          Image
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="admin-dash__email-rich-file"
          tabIndex={-1}
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (f) insertImageFromFile(f);
          }}
        />
      </div>
      <div
        ref={ref}
        className="admin-dash__email-rich-editor"
        contentEditable={!disabled}
        role="textbox"
        aria-multiline
        aria-placeholder={placeholder}
        data-placeholder={placeholder}
        suppressContentEditableWarning
        onInput={onInput}
        onBlur={emit}
        onPaste={onPaste}
      />
      {empty ? <span className="admin-dash__email-rich-placeholder" aria-hidden>{placeholder}</span> : null}
      <div className="admin-dash__email-rich-foot">
        <span className="admin-dash__email-char-hint">{charCount} characters (plain text)</span>
        <span className="admin-dash__email-rich-foot-hint">HTML is sent as-is; your API may strip tags for plain mail.</span>
      </div>
    </div>
  );
}

export default AdminEmailRichComposer;
